<script setup>
// "Publisher spotlight" homepage section: CMS-driven from the Strapi
// cld-publisher collection (featured entries, via the Nitro proxy which owns
// caching + locale fallback). Each card is a publisher with a visual, optional
// date badge/emoji, pitch and a link to its /publisher page.
// Renders nothing while no CMS data exists. Card styling mirrors HomeKeyMoments.
const { locale } = useI18n();
const localePath = useLocalePath();

const { data: publishers } = useFetch(
  () => `/api/cms/cld-publishers?locale=${locale.value}`,
  {
    key: "cms-publisher-spotlight",
    watch: [locale],
    default: () => [],
  },
);

const cards = computed(() => (publishers.value ?? []).slice(0, 5));

const publisherLink = (card) => localePath(`/publisher/${card.slug}`);

// Gradient fallbacks for cards without an uploaded image (same set as
// HomeKeyMoments).
const fallbackBg = [
  'linear-gradient(135deg, #0a1018, #314568)',
  'linear-gradient(135deg, #f1bd50, #163a6b)',
  'linear-gradient(135deg, #5c3db6, #ffd943)',
  'linear-gradient(135deg, #151515, #c55c20)',
  'linear-gradient(135deg, #81392f, #ead5bb)',
];

const dateBadge = (date) => {
  if (!date) return null;
  try {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    return {
      day: String(d.getDate()).padStart(2, '0'),
      month: d.toLocaleDateString(locale.value, { month: 'short' }).replace('.', '').toUpperCase(),
    };
  } catch (e) {
    return null;
  }
};
</script>

<template>
  <section v-if="cards.length" class="container mx-auto px-4 mt-6">
    <div class="rounded-3xl border border-[var(--color-primary-100)] p-5 md:p-6 publishers-panel">
      <!-- Section head: kicker + title + description -->
      <div class="mb-4">
        <p class="publishers-accent text-xs font-bold uppercase tracking-widest mb-1">{{ $t('publisher.spotlight_kicker') }}</p>
        <h2 class="text-2xl md:text-3xl font-semibold text-gray-900 m-0">{{ $t('publisher.spotlight_title') }}</h2>
        <p class="text-sm text-gray-500 mt-1 max-w-3xl">{{ $t('publisher.spotlight_desc') }}</p>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <article v-for="(card, i) in cards" :key="card.slug"
          class="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <NuxtLink :to="publisherLink(card)" class="block">
            <!-- Visual: uploaded image or gradient fallback, date badge + icon -->
            <div class="relative h-28" :style="card.imageUrl ? '' : `background:${fallbackBg[i % fallbackBg.length]}`">
              <img v-if="card.imageUrl" :src="card.imageUrl" :alt="card.name"
                class="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
              <span v-if="dateBadge(card.date)"
                class="absolute top-2.5 left-2.5 bg-white/95 rounded-lg px-2 py-1 text-center leading-none shadow-sm">
                <b class="block text-base text-gray-900">{{ dateBadge(card.date).day }}</b>
                <span class="text-[10px] font-bold text-gray-600">{{ dateBadge(card.date).month }}</span>
              </span>
              <span v-if="card.icon" class="absolute right-3 bottom-1 text-5xl select-none" aria-hidden="true">{{ card.icon }}</span>
            </div>
            <!-- Body: title + pitch + link -->
            <div class="p-3.5">
              <h3 class="text-sm font-semibold text-gray-900 m-0 mb-1">{{ card.title }}</h3>
              <p v-if="card.description" class="text-xs text-gray-500 m-0 mb-2 line-clamp-2">{{ card.description }}</p>
              <span class="publishers-accent text-xs font-bold">{{ $t('explore_selection') }} →</span>
            </div>
          </NuxtLink>
        </article>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Soft mint panel, same treatment as HomeKeyMoments. */
.publishers-panel {
  background: linear-gradient(135deg, var(--color-primary-50), #f8fffc);
}

.publishers-accent {
  color: #008556;
}

/* Dark theme: the mint panel must flip too, otherwise dark.css turns the
   heading near-white on a light background (unreadable). */
html.dark .publishers-panel {
  background: linear-gradient(135deg, #101b14, var(--cld-dark-bg));
  border-color: var(--cld-dark-border) !important;
}

html.dark .publishers-accent {
  color: var(--color-primary-300);
}

html.dark .publishers-panel .bg-white\/95 {
  background-color: rgba(19, 27, 22, 0.92) !important;
}
</style>
